import { useCallback, useEffect, useRef, useState } from 'react';

import { ANONYMOUS } from '@tosspayments/tosspayments-sdk';

import { createPaymentWidgets } from './index';
import { TossPaymentRequestParams } from './types';

interface UseTossPaymentWidgetParams {
  customerKey: string | typeof ANONYMOUS;
  amount: number;
}

type WidgetsInstance = Awaited<ReturnType<typeof createPaymentWidgets>>;
type PaymentMethodWidget = Awaited<ReturnType<WidgetsInstance['renderPaymentMethods']>>;

export function useTossPaymentWidget({
  customerKey,
  amount,
}: UseTossPaymentWidgetParams) {
  const widgetsRef = useRef<WidgetsInstance | null>(null);
  const paymentMethodWidgetRef = useRef<PaymentMethodWidget | null>(null);
  const [isReady, setIsReady] = useState(false);

  // SDK 초기화 → widgets 인스턴스 생성 → 금액 설정
  useEffect(() => {
    let cancelled = false;

    async function init() {
      const widgets = await createPaymentWidgets(customerKey as string);
      if (cancelled) return;

      await widgets.setAmount({ currency: 'KRW', value: amount });
      if (cancelled) return;

      widgetsRef.current = widgets;
      setIsReady(true);
    }

    init();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerKey]);

  // 결제 UI 렌더링
  const renderPaymentMethods = useCallback(
    async (selector: string, variantKey = 'DEFAULT') => {
      if (!widgetsRef.current) return;
      const widget = await widgetsRef.current.renderPaymentMethods({
        selector,
        variantKey,
      });
      paymentMethodWidgetRef.current = widget;
      return widget;
    },
    [],
  );

  // 결제 요청 (Redirect)
  const requestPayment = useCallback(async (params: TossPaymentRequestParams) => {
    if (!widgetsRef.current) return;
    await widgetsRef.current.requestPayment(params);
  }, []);

  // 금액 변경
  const updateAmount = useCallback(async (newAmount: number) => {
    if (!widgetsRef.current) return;
    await widgetsRef.current.setAmount({ currency: 'KRW', value: newAmount });
  }, []);

  // 언마운트 시 위젯 destroy
  useEffect(() => {
    return () => {
      paymentMethodWidgetRef.current?.destroy();
      widgetsRef.current = null;
    };
  }, []);

  return {
    isReady,
    renderPaymentMethods,
    requestPayment,
    updateAmount,
  };
}
